import { useEffect, useState } from 'react';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Switch, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useUpdateAiModel } from '../../../../data/useAiModels';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { BUCKET_PATTERN, CONTEXT_MAX, CONTEXT_MIN, MAX_OUTPUT_MAX, MAX_OUTPUT_MIN, REGION_PATTERN } from '../aiModels';

type Field = 'name' | 'description' | 'model_id' | 'region' | 'accepts_images' | 'max_output_tokens' | 'context_tokens' | 'outside_south_africa' | 'sort_order' | 'storage_bucket';
const FIELDS: readonly Field[] = ['name', 'description', 'model_id', 'region', 'accepts_images', 'max_output_tokens', 'context_tokens', 'outside_south_africa', 'sort_order', 'storage_bucket'];

/** The service's refusal, as `{ field, message }`, when it names a field this drawer shows. */
function fieldRefusal(e: unknown): { field: Field | null; message: string } | null {
  if (!isApiError(e)) return null;
  const err = (e.body as { error?: { field?: string; message?: string } } | null)?.error;
  const field = FIELDS.find((f) => f === err?.field) ?? null;
  return { field, message: err?.message ?? e.message };
}

/**
 * How a model is reached and what businesses are told about it: its name and description, the Bedrock model id and
 * region, whether it reads pictures, its longest answer, whether it runs outside South Africa, where it sits in the
 * list and, for speech to text, the S3 bucket recordings wait in. Only the fields that changed are sent. A model that is on and is pointed at a different model id or
 * region confirms first, because every business using it moves with it.
 */
export function AiModelEditDrawer({ model, onClose }: { model: admin.AdminAiModel | null; onClose: () => void }) {
  const t = useT();
  const update = useUpdateAiModel();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [modelId, setModelId] = useState('');
  const [region, setRegion] = useState('');
  const [acceptsImages, setAcceptsImages] = useState(false);
  const [maxOutput, setMaxOutput] = useState<number | ''>('');
  // The context window (30 October 2026): Suggest flows with AI keeps each call under half of it. 0 is "not known".
  const [contextTokens, setContextTokens] = useState<number | ''>(0);
  const [outside, setOutside] = useState(false);
  const [sortOrder, setSortOrder] = useState<number | ''>(0);
  const [bucket, setBucket] = useState('');
  const [refusal, setRefusal] = useState<{ field: Field | null; message: string } | null>(null);

  useEffect(() => {
    if (!model) return;
    setName(model.name);
    setDescription(model.description ?? '');
    setModelId(model.model_id);
    setRegion(model.region ?? '');
    setAcceptsImages(model.accepts_images);
    setMaxOutput(model.max_output_tokens);
    setContextTokens(model.context_tokens ?? 0);
    setOutside(model.outside_south_africa);
    setSortOrder(model.sort_order);
    setBucket(model.storage_bucket ?? '');
    setRefusal(null);
  }, [model]);

  if (!model) return <Drawer opened={false} onClose={onClose} position="right" size="md" />;

  const isText = model.kind === 'text';
  const isTranscribe = model.kind === 'transcribe';

  // Checked as the operator types, with the service's own limits; the service checks again on save.
  const nameError = name.trim().length === 0 || name.trim().length > 80 ? t('billing.aiModels.edit.nameInvalid') : undefined;
  const regionError = region.trim() && !REGION_PATTERN.test(region.trim()) ? t('billing.aiModels.edit.regionInvalid') : undefined;
  const maxError = isText && (typeof maxOutput !== 'number' || !Number.isInteger(maxOutput) || maxOutput < MAX_OUTPUT_MIN || maxOutput > MAX_OUTPUT_MAX)
    ? t('billing.aiModels.edit.maxInvalid', { min: formatInteger(MAX_OUTPUT_MIN), max: formatInteger(MAX_OUTPUT_MAX) })
    : undefined;
  const contextError = isText && (typeof contextTokens !== 'number' || !Number.isInteger(contextTokens) || (contextTokens !== 0 && (contextTokens < CONTEXT_MIN || contextTokens > CONTEXT_MAX)))
    ? t('billing.aiModels.edit.contextInvalid', { min: formatInteger(CONTEXT_MIN), max: formatInteger(CONTEXT_MAX) })
    : undefined;
  const sortError = typeof sortOrder !== 'number' || !Number.isInteger(sortOrder) ? t('billing.aiModels.edit.sortInvalid') : undefined;
  const bucketError = isTranscribe && bucket.trim() && !BUCKET_PATTERN.test(bucket.trim()) ? t('billing.aiModels.edit.bucketInvalid') : undefined;
  const invalid = Boolean(nameError || regionError || maxError || contextError || sortError || bucketError);

  const change: admin.UpdateAiModelRequest = {};
  if (name.trim() !== model.name) change.name = name.trim();
  if (description.trim() !== (model.description ?? '')) change.description = description.trim();
  if (modelId.trim() !== model.model_id) change.model_id = modelId.trim();
  if (region.trim() !== (model.region ?? '')) change.region = region.trim();
  if (isText && acceptsImages !== model.accepts_images) change.accepts_images = acceptsImages;
  if (isText && typeof maxOutput === 'number' && maxOutput !== model.max_output_tokens) change.max_output_tokens = maxOutput;
  if (isText && typeof contextTokens === 'number' && contextTokens !== (model.context_tokens ?? 0)) change.context_tokens = contextTokens;
  if (outside !== model.outside_south_africa) change.outside_south_africa = outside;
  if (typeof sortOrder === 'number' && sortOrder !== model.sort_order) change.sort_order = sortOrder;
  if (isTranscribe && bucket.trim() !== (model.storage_bucket ?? '')) change.storage_bucket = bucket.trim();
  const dirty = Object.keys(change).length > 0;

  /** The service's message under the field it names; anything else goes in the alert at the foot. */
  const errorFor = (field: Field, local?: string) => (refusal?.field === field ? refusal.message : local);

  function send(m: admin.AdminAiModel) {
    setRefusal(null);
    update.mutate({ code: m.code, change }, {
      onSuccess: () => {
        notifySuccess(t('billing.aiModels.edit.saved', { name: change.name ?? m.name }), t('billing.aiModels.title'));
        onClose();
      },
      onError: (e) => setRefusal(fieldRefusal(e) ?? { field: null, message: e instanceof Error ? e.message : t('common.unknownError') })
    });
  }

  function submit() {
    if (!model || invalid || !dirty) return;
    const moves = model.enabled && (change.model_id !== undefined || change.region !== undefined);
    const leaves = model.enabled && change.outside_south_africa === true;
    if (!moves && !leaves) { send(model); return; }
    openConfirmModal({
      title: t('billing.aiModels.edit.confirm.title', { name: model.name }),
      children: (
        <Stack gap="sm">
          {moves && <Text role="bodySmall">{t('billing.aiModels.edit.confirm.moves', { name: model.name })}</Text>}
          {leaves && <Text role="bodySmall">{t('billing.aiModels.edit.confirm.leaves', { name: model.name })}</Text>}
        </Stack>
      ),
      labels: { confirm: t('billing.aiModels.edit.confirm.submit'), cancel: t('action.cancel') },
      confirmProps: { color: leaves ? 'red' : 'orange' },
      onConfirm: () => send(model)
    });
  }

  return (
    <Drawer opened onClose={onClose} title={t('billing.aiModels.edit.title', { name: model.name })} position="right" size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('billing.aiModels.edit.intro', { code: model.code, kind: t(`billing.aiModels.kind.${model.kind}`, model.kind) })}
        </Text>
        <TextInput
          label={t('billing.aiModels.edit.name')}
          value={name}
          onChange={(e) => { setName(e.currentTarget.value); setRefusal(null); }}
          required
          description={t('billing.aiModels.edit.nameHint')}
          error={errorFor('name', nameError)}
        />
        <Textarea
          label={t('billing.aiModels.edit.description')}
          value={description}
          onChange={(e) => { setDescription(e.currentTarget.value); setRefusal(null); }}
          autosize
          minRows={2}
          maxRows={6}
          description={t('billing.aiModels.edit.descriptionHint')}
          error={errorFor('description')}
        />
        <TextInput
          label={t('billing.aiModels.edit.modelId')}
          value={modelId}
          onChange={(e) => { setModelId(e.currentTarget.value); setRefusal(null); }}
          placeholder={isText ? t('billing.aiModels.modelId.platform') : undefined}
          description={isText ? t('billing.aiModels.edit.modelIdHint') : t('billing.aiModels.edit.modelIdHintOther')}
          error={errorFor('model_id')}
        />
        <TextInput
          label={t('billing.aiModels.edit.region')}
          value={region}
          onChange={(e) => { setRegion(e.currentTarget.value); setRefusal(null); }}
          placeholder={t('billing.aiModels.region.platform')}
          description={t('billing.aiModels.edit.regionHint')}
          error={errorFor('region', regionError)}
        />
        {isTranscribe && (
          <TextInput
            label={t('billing.aiModels.edit.bucket')}
            value={bucket}
            onChange={(e) => { setBucket(e.currentTarget.value); setRefusal(null); }}
            description={t('billing.aiModels.edit.bucketHint')}
            error={errorFor('storage_bucket', bucketError)}
          />
        )}
        {isText && (
          <NumberInput
            label={t('billing.aiModels.edit.maxOutput')}
            value={maxOutput}
            onChange={(v) => { setMaxOutput(typeof v === 'number' ? v : ''); setRefusal(null); }}
            min={MAX_OUTPUT_MIN}
            step={256}
            description={t('billing.aiModels.edit.maxOutputHint', { min: formatInteger(MAX_OUTPUT_MIN), max: formatInteger(MAX_OUTPUT_MAX) })}
            error={errorFor('max_output_tokens', maxError)}
          />
        )}
        {isText && (
          <NumberInput
            label={t('billing.aiModels.edit.context')}
            value={contextTokens}
            onChange={(v) => { setContextTokens(typeof v === 'number' ? v : ''); setRefusal(null); }}
            min={0}
            step={1000}
            description={t('billing.aiModels.edit.contextHint')}
            error={errorFor('context_tokens', contextError)}
            data-testid="ai-model-context"
          />
        )}
        {isText && (
          <Switch
            label={t('billing.aiModels.edit.acceptsImages')}
            checked={acceptsImages}
            onChange={(v) => { setAcceptsImages(v); setRefusal(null); }}
            description={t('billing.aiModels.edit.acceptsImagesHint')}
            error={errorFor('accepts_images')}
          />
        )}
        <Switch
          label={t('billing.aiModels.edit.outside')}
          checked={outside}
          onChange={(v) => { setOutside(v); setRefusal(null); }}
          description={t('billing.aiModels.edit.outsideHint')}
          error={errorFor('outside_south_africa')}
        />
        <NumberInput
          style={{ maxWidth: 200 }}
          label={t('billing.aiModels.edit.sortOrder')}
          value={sortOrder}
          onChange={(v) => { setSortOrder(typeof v === 'number' ? v : ''); setRefusal(null); }}
          step={10}
          description={t('billing.aiModels.edit.sortOrderHint')}
          error={errorFor('sort_order', sortError)}
        />
        {refusal && refusal.field === null && (
          <Alert color="error" title={t('billing.aiModels.edit.failed')}>{refusal.message}</Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={update.isPending} disabled={invalid || !dirty} onClick={submit}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
